import { useState, useEffect, ReactNode, useCallback, useContext } from 'react';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { Typography } from '../../components/typography/typography';
import { PaginationData } from '../../clients/quipli-service/pagination/pagination';
import { DataTable, DataTableRow, DataTableColumn } from '../../components/table/data-table';
import { Location } from '../../clients/quipli-service/location/location';
import { SearchBar } from '../../components/search-bar/search-bar';
import { SearchDropdownOption } from '../../components/dropdown/search-dropdown';
import { Dictionary, debounce, keyBy } from 'lodash';
import { DateTime } from 'luxon';
import { SelectForm } from '../../components/select-form/select-form';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { SearchProductInventoryUnitsRequest } from '../../clients/quipli-service/product/product';
import { Category } from '../../clients/quipli-service/category/category';
import { useCategoryApiV1 } from '../../clients/quipli-service/category/category-api-v1';
import { OrderScheduleEntry } from '../../clients/quipli-service/availability/availability';
import classNames from 'classnames';
import { Tooltip } from '../../components/tooltip/tooltip';
import { ScheduleTooltip } from '../inventory-dashboard/schedule-tooltip/schedule-tooltip';
import { toast } from '../../util/toasts';
import './product-units-list.scss';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../hooks/use-request-query-params';
import { useReportApiV2 } from '../../clients/quipli-service/report/report-api-v2';
import { ProductUnitsReportRow } from '../../clients/quipli-service/report/report';
import { Button } from '../../components/form/button';
import Icon from '../../components/icons/icon';
import FileSaver from 'file-saver';
import { displayMoneyFormat } from '../../util/util';
import { TenantContext } from '../../context/tenant-context';
import { SpinnerModal } from '../../components/modal/spinner-modal';
import { Checkbox } from '../../components/checkbox/checkbox';

export const ProductUnitsList = () => {
  const { listLocations, getMultipleConfigurations } = useAPI();
  const { listCategories } = useCategoryApiV1();
  const { productUnitsReport, exportProductUnitsReport } = useReportApiV2();
  const { configuration } = useContext(TenantContext);
  const currency = configuration?.currency;

  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [tablePagination, setTablePagination] = useState<PaginationData>();
  const [locations, setLocations] = useState<Location[]>();
  const [categories, setCategories] = useState<Category[]>();
  const [configurations, setConfigurations] = useState<Dictionary<Configuration>>({});
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [summaryData, setSummaryData] = useState<{
    total: number;
    onRent: number;
    available: number;
    hardDown: number;
    softDown: number;
    offRent: number;
  }>({ total: 0, onRent: 0, available: 0, hardDown: 0, softDown: 0, offRent: 0 });

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<SearchProductInventoryUnitsRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
      categoryIds: params.has('categoryIds') ? [parseInt(params.get('categoryIds')!)] : undefined,
      locationIds: params.has('locationIds') ? [parseInt(params.get('locationIds')!)] : undefined,
      sort: {
        field: 'serialNumber',
        direction: 'asc',
      },
      forRent: true,
      forSale: true,
      includeSoldUnits: params.has('includeSoldUnits') ? params.get('includeSoldUnits') === 'true' : false,
    },
    () => {},
  );

  const fetchProductUnits = useCallback(
    debounce(async (req: SearchProductInventoryUnitsRequest) => {
      setIsTableDataLoading(true);

      try {
        const { items, pagination } = await productUnitsReport({
          ...req,
          pageNumber: 1,
          pageSize: 10000,
        });

        setTablePagination(() => ({
          total: pagination.total,
          pageNumber: req.pageNumber || tablePagination?.pageNumber || 1,
          pageSize: req.pageSize || tablePagination?.pageSize || 10,
          lastPage: Math.ceil(pagination.total / (req.pageSize || tablePagination?.pageSize || 10)),
          prevPage:
            (req.pageNumber || tablePagination?.pageNumber)! > 1
              ? (req.pageNumber || tablePagination?.pageNumber)! - 1
              : null,
          nextPage:
            (req.pageNumber || tablePagination?.pageNumber)! <
            Math.ceil(pagination.total / (req.pageSize || tablePagination?.pageSize || 10))
              ? (req.pageNumber || tablePagination?.pageNumber)! + 1
              : null,
        }));

        if (items.length > 0) {
          const data = items.map((unit) => {
            return buildDataRow(unit);
          });
          setData(data.map((r) => r.row));
          const sd: { [key: string]: number } = {
            'On Rent': 0,
            Available: 0,
            'Hard Down': 0,
            'Soft Down': 0,
            'Off Rent': 0,
          };
          data.forEach((d) => {
            if (d.countFor) sd[d.countFor] += 1;
          });

          setSummaryData({
            total: items.filter((pu) => pu.isActive && !pu.soldAt).length,
            available: sd.Available,
            onRent: sd['On Rent'],
            hardDown: sd['Hard Down'],
            softDown: sd['Soft Down'],
            offRent: sd['Off Rent'],
          });
          setIsTableDataLoading(false);
        } else {
          setData([]);
        }
      } catch (err) {
        toast('Error loading product units', 'error');
        console.error(err);
      } finally {
        setIsTableDataLoading(false);
      }
    }, 600),
    [tablePagination],
  );

  function unitStatus(unit: ProductUnitsReportRow): { currentStatus: ReactNode; currentRenter: ReactNode } {
    if (unit.soldAt) {
      return {
        currentStatus: (
          <div className="unit-status hover:cursor-default">
            <div className="status-label sold">Sold</div>
          </div>
        ),
        currentRenter: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            Sold {unit.soldAt}
          </Typography>
        ),
      };
    }
    if (!unit.soldAt && unit.type === 'Sale' && unit.currentScheduled?.currentStatus !== 'Hard Down') {
      return {
        currentStatus: (
          <div className="unit-status hover:cursor-default">
            <div className="status-label available">Available</div>
          </div>
        ),
        currentRenter: undefined,
      };
    }
    if (!unit.currentScheduled || !unit.currentScheduled.currentStatus)
      return { currentStatus: undefined, currentRenter: undefined };

    const { currentStatus, currentEvent } = unit.currentScheduled;
    const content = (
      <div className="unit-status hover:cursor-default">
        <div className={`status-label ${currentStatus.toLowerCase().replace(' ', '-')}`}>{currentStatus}</div>
        {currentEvent?.to && currentStatus != 'Off Rent' && (
          <Typography
            type="text-xs"
            weight="regular"
            className={classNames('status-until', {
              'is-past-due': currentEvent.to.diff(DateTime.now()).milliseconds < 0,
            })}
          >
            Until: {currentEvent.to?.toFormat('D t')}
          </Typography>
        )}
      </div>
    );

    const status = currentEvent ? (
      <Tooltip tooltip={<ScheduleTooltip scheduleEntry={currentEvent} />}>{content}</Tooltip>
    ) : (
      content
    );
    const currentRenter =
      currentStatus === 'On Rent' ? (
        <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
          {(currentEvent as OrderScheduleEntry).renterName}
        </Typography>
      ) : undefined;
    return { currentStatus: status, currentRenter };
  }

  function nextEvent(unit: ProductUnitsReportRow): ReactNode {
    if (!unit.nextScheduled) return undefined;

    let label;
    switch (unit.nextScheduled.type) {
      case 'WorkOrder':
        label = 'Work Order';
        break;
      case 'Sold':
        label = 'Sold';
        break;
      case 'Order':
        label = 'Order';
        break;
      case 'NotInService':
        label = 'Not In Service';
        break;
    }
    return (
      <Tooltip tooltip={<ScheduleTooltip scheduleEntry={unit.nextScheduled} />}>
        <div className="unit-next-event hover:cursor-default">
          <div className={`event-label ${label.toLowerCase().replace(' ', '-')}`}>{label}</div>
          {unit.nextScheduled?.to && (
            <Typography
              type="text-xs"
              weight="regular"
              className={classNames('event-start', {
                'is-past-due': unit.nextScheduled.from.diff(DateTime.now()).milliseconds < 0,
              })}
            >
              At: {unit.nextScheduled.from?.toFormat('D t')}
            </Typography>
          )}
        </div>
      </Tooltip>
    );
  }

  let columns: DataTableColumn[] = [
    { key: 'unitNumber', label: 'Unit No.' },
    { key: 'serialNumber', label: 'Serial No.' },
    { key: 'product', label: 'Product' },
  ];
  if (configurations['CONF_SALE_SERIAL_TRACKING']?.value === '1') {
    columns = [...columns, { key: 'type', label: 'Type', showRule: 'Both' }];
  }
  columns = columns.concat([
    { key: 'category', label: 'Category', showRule: 'Both' },
    { key: 'location', label: 'Location', showRule: 'Both' },
    { key: 'status', label: 'Status' },
    { key: 'currentRenter', label: 'Current Renter', showRule: 'Both' },
    { key: 'nextEvent', label: 'Next Event', showRule: 'Both' },
  ]);
  if (configurations['CONF_BOOM_AND_BUCKET_INTEGRATION']?.value === '1') {
    columns = [
      ...columns,
      { key: 'makeModelYear', label: 'Make/Model/Year', showRule: 'Both' },
      { key: 'marketValue', label: 'Market Value', showRule: 'Both' },
    ];
  }
  if (configurations['CONF_SALE_SERIAL_TRACKING']?.value === '1' && !request.forRent && request.forSale) {
    columns = [...columns, { key: 'soldAt', label: 'Sold At', showRule: 'Both' }];
  }

  const buildDataRow = (unit: ProductUnitsReportRow) => {
    const { currentStatus, currentRenter } = unitStatus(unit);
    return {
      countFor: unit.isActive ? unit.currentScheduled?.currentStatus : undefined,
      row: {
        mobileHeader: (
          <>
            <Typography type="text-md" weight="medium">
              Unit No. {unit.unitNumber}
              {/* TODO: maybe serial number too? */}
            </Typography>
            <Typography type="text-sm" weight="medium">
              <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
                {currentStatus}
              </Typography>
            </Typography>
          </>
        ),
        fields: {
          unitNumber: (
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {unit.unitNumber}
            </Typography>
          ),
          serialNumber: (
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {unit.serialNumber}
            </Typography>
          ),
          product: (
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {unit.productName}
            </Typography>
          ),
          type: (
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {unit.type}
            </Typography>
          ),
          category: (
            <>
              {unit.categories.map((category) => {
                return (
                  <Typography key={category} type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
                    {category}
                  </Typography>
                );
              })}
            </>
          ),
          location: (
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {unit.locationName}
            </Typography>
          ),
          makeModelYear: (
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {unit.makeModelYear ?? '-'}
            </Typography>
          ),
          marketValue: (
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {unit.marketValue ? displayMoneyFormat(unit.marketValue, currency) : '-'}
            </Typography>
          ),
          status: unit.isActive && currentStatus,
          currentRenter: unit.isActive && currentRenter,
          nextEvent: unit.isActive && nextEvent(unit),
          soldAt:
            configurations['CONF_SALE_SERIAL_TRACKING']?.value === '1' && request.forSale ? (
              <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
                {unit.soldAt}
              </Typography>
            ) : (
              <></>
            ),
        },
        data: unit,
        className: classNames({ '!bg-grey-200': !unit.isActive }),
      },
    };
  };

  const locationOptions: SearchDropdownOption[] = (locations ?? []).map(
    (loc) =>
      ({
        label: loc.name,
        value: loc.id.toString(),
      } as SearchDropdownOption),
  );

  const categoryOptions: SearchDropdownOption[] = (categories ?? []).map(
    (category) =>
      ({
        label: category.name,
        value: category.id.toString(),
      } as SearchDropdownOption),
  );

  useEffect(() => {
    const fetchStuff = async () => {
      if (!locations) {
        const _locations = await listLocations(true);
        setLocations(_locations);
      }
      if (!categories) {
        setCategories(await listCategories(false));
      }
      if (Object.keys(configurations).length === 0) {
        setConfigurations(
          keyBy(
            await getMultipleConfigurations([
              'CONF_WORK_ORDERS',
              'CONF_UNIT_TRACKING_VIA',
              'CONF_BOOM_AND_BUCKET_INTEGRATION',
              'CONF_SALE_SERIAL_TRACKING',
            ]),
            'variable',
          ),
        );
      }
    };

    fetchStuff();
  });

  useEffect(() => {
    if (request.pageNumber !== undefined && request.pageSize !== undefined) {
      setTablePagination((prevState) => ({
        total: prevState?.total || 0,
        pageNumber: request.pageNumber!,
        pageSize: request.pageSize!,
        prevPage: request.pageNumber! > 1 ? request.pageNumber! - 1 : null,
        nextPage:
          prevState && request.pageNumber! < Math.ceil(prevState.total / request.pageSize!)
            ? request.pageNumber! + 1
            : null,
        lastPage: prevState ? Math.ceil(prevState.total / request.pageSize!) : 1,
      }));
      setIsTableDataLoading(false);
    }
  }, [request.pageNumber, request.pageSize]);

  useEffect(() => {
    if (locations && categories) {
      fetchProductUnits(request);
    }
  }, [request.keyword, request.categoryIds, request.locationIds, locations, categories, request.includeSoldUnits]);

  const doExport = async () => {
    setIsExporting(true);
    try {
      const file = await exportProductUnitsReport(request);

      const exportFileName = `Product_Units_Report${
        request.locationIds?.length || request.categoryIds?.length
          ? `__${request.locationIds
              ?.map((loc) => locations?.find((l) => l.id === loc)?.name ?? '')
              .join('_')}_${request.categoryIds
              ?.map((cat) => categories?.find((c) => c.id === cat)?.name ?? '')
              .join('_')}`
          : ''
      }`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (err) {
      toast('Error exporting product units', 'error');
      console.error(err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="product-units-list container-v2 pb-5">
      <div className="flex justify-between items-center">
        <Typography type="display-xs" weight="medium" className="text-[var(--grey-800)]">
          Product Units
        </Typography>
      </div>

      <div className="pt-2 flex justify-between gap-2">
        <div className="flex gap-2 flex-wrap">
          <SearchBar
            data-cy="search-product-units"
            placeholder="Search by keyword"
            subtitle="Unit number, serial number, product name"
            defaultValue={request.keyword}
            setRequest={setRequest}
          />

          <div className="col-span-1 sm:col-span-1">
            <SelectForm
              className="filter-dropdown"
              options={categoryOptions}
              onChange={(c) => {
                setRequest((prevState) => ({ ...prevState, categoryIds: c ? [+c.value] : undefined }));
              }}
              isClearable={true}
              placeholder="Category"
              value={categoryOptions.find((c) => c.value === request.categoryIds?.[0].toString())}
            />
          </div>
          <div className="col-span-1 sm:col-span-1">
            <SelectForm
              className="filter-dropdown"
              options={locationOptions}
              onChange={(l) => {
                setRequest((prevState) => ({ ...prevState, locationIds: l ? [+l.value] : undefined }));
              }}
              isClearable={true}
              placeholder="Location"
              value={locationOptions.find((l) => l.value === request.locationIds?.[0].toString())}
            />
          </div>
          <div
            className="flex flex-row gap-2 cursor-pointer items-center mb-4"
            onClick={() => setRequest((prev) => ({ ...prev, includeSoldUnits: !request.includeSoldUnits }))}
          >
            <Typography type="text-sm" weight="regular">
              Include Sold Units
            </Typography>
            <Checkbox isChecked={request.includeSoldUnits} />
          </div>
        </div>

        <Button className="button-yellow h-[40px]" onClick={() => doExport()} data-cy="export">
          Export
          <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
        </Button>
      </div>

      <div className="flex w-full flex-wrap mb-3">
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Current Utilization %
          </Typography>
          {isTableDataLoading || !summaryData ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {summaryData.total === 0 ? 0 : ((summaryData.onRent / summaryData.total) * 100).toFixed(2)}%
            </Typography>
          )}
        </div>

        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Total Units
          </Typography>
          {isTableDataLoading || !summaryData ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {summaryData.total}
            </Typography>
          )}
        </div>

        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            On Rent
          </Typography>
          {isTableDataLoading || !summaryData ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {summaryData.onRent}
            </Typography>
          )}
        </div>

        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Off Rent
          </Typography>
          {isTableDataLoading || !summaryData ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {summaryData.offRent}
            </Typography>
          )}
        </div>

        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Available
          </Typography>
          {isTableDataLoading || !summaryData ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {summaryData.available}
            </Typography>
          )}
        </div>

        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Hard Down
          </Typography>
          {isTableDataLoading || !summaryData ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {summaryData.hardDown}
            </Typography>
          )}
        </div>

        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Soft Down
          </Typography>
          {isTableDataLoading || !summaryData ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {summaryData.softDown}
            </Typography>
          )}
        </div>
      </div>

      <DataTable
        isLoading={isTableDataLoading}
        columnNames={columns}
        data={
          data && tablePagination
            ? data.slice(
                (tablePagination.pageNumber - 1) * tablePagination.pageSize,
                tablePagination.pageNumber * tablePagination.pageSize,
              )
            : []
        }
        pagination={tablePagination}
        request={request}
        setRequest={setRequest}
        cypressPrevious="previous_product_units_page"
        cypressNext="next_product_units_page"
      />
      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Product Units'} />
    </div>
  );
};
